import type { ActivityDto, OnboardingMessageDto, ProcessCandidateDto, ReviewCard } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTenant } from '../../lib/useTenant';
import { SoftwareActions } from './Builder';

/** Avanzamento di un lavoro lungo: i passi fatti, quello in corso e da quanto tempo si lavora. */
export function ActivityLoader({
  activity,
  fallback,
}: {
  activity: ActivityDto | null;
  fallback: string;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds = activity
    ? Math.max(0, Math.round((now - new Date(activity.started_at).getTime()) / 1000))
    : 0;
  const steps = activity?.steps ?? [{ label: fallback, done: false }];
  return (
    <div
      className="rounded-xl border border-accent/20 bg-accent/5 px-4 py-3 text-sm"
      role="status"
      aria-live="polite"
    >
      <ul className="space-y-1.5">
        {steps.map((st, i) => (
          <li
            key={i}
            className={`flex items-start gap-2 ${st.done ? 'text-ink/45' : 'text-ink/85'}`}
          >
            {st.done ? (
              <span className="mt-0.5 w-4 shrink-0 text-center text-emerald-600">✓</span>
            ) : (
              <span className="mt-1 h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            )}
            <span>{st.label}</span>
          </li>
        ))}
      </ul>
      {activity && (
        <p className="mt-2 text-xs text-ink/45">
          {seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`} ·
          puoi lasciare la pagina, il lavoro continua
        </p>
      )}
    </div>
  );
}

/** Elenco numerato dell'area in verifica: si risponde con i numeri. */
export function ReviewList({ card, current }: { card: ReviewCard; current: boolean }) {
  return (
    <div
      className={`rounded-xl border p-3 text-sm ${current ? 'border-accent/30 bg-white' : 'border-black/10 bg-black/[0.02] opacity-80'}`}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/50">
        Procedure proposte per {card.area}
      </p>
      <ol className="space-y-1">
        {card.items.map((i) => (
          <li
            key={i.key}
            className={`flex gap-2 ${i.stato === 'scartato' ? 'text-ink/35 line-through' : ''}`}
          >
            <span className="w-6 shrink-0 text-right font-semibold tabular-nums">{i.n}.</span>
            <span className="min-w-0 flex-1">
              {i.name}
              {i.description && <span className="block text-xs text-ink/50">{i.description}</span>}
            </span>
            {i.stato === 'confermato' && <span className="text-emerald-600">✓</span>}
          </li>
        ))}
      </ol>
      {current && (
        <p className="mt-2 text-xs text-ink/50">
          Rispondi con i numeri, ad esempio «1, 2 e 4 sì, il 3 no», e raccontami quelli che fate in
          modo diverso.
        </p>
      )}
    </div>
  );
}

/** Bozza in scrittura (o in attesa di esserlo): le procedure proposte aspettano la conferma nella chat dell'area. */
export const drafting = (c: ProcessCandidateDto) =>
  c.status === 'in_elaborazione' ||
  (!c.error &&
    !c.conversation_id &&
    (c.status === 'pronto' || (c.status === 'individuato' && c.details.origine === 'raccontato')));

/** Un messaggio della conversazione (principale o di un'area). */
/** I titoli dei passi della configurazione, per dire dove è cambiato qualcosa. */
const STEP_TITLES: Partial<Record<string, string>> = {
  visura: 'Visura',
  impresa: 'L’impresa',
  sedi: 'Sedi',
  attivita: 'Attività economiche',
  impianti: 'Impianti e attrezzature',
  software: 'Programmi e servizi online',
  organizzazione: 'Organizzazione e ruoli',
  procedure: 'Procedure',
};

export function MessageBubble({
  m,
  card,
  actions,
}: {
  m: OnboardingMessageDto;
  card: boolean;
  actions: boolean;
}) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  return m.role === 'user' ? (
    <div className="ml-auto max-w-[85%] space-y-1">
      <div className="whitespace-pre-line rounded-2xl rounded-br-sm bg-accent px-4 py-2 text-sm text-white">
        {m.content}
      </div>
      {m.changes.allegati?.length ? (
        <p className="text-right text-xs text-ink/50">
          📎 {m.changes.allegati.map((a) => a.titolo).join(', ')}
        </p>
      ) : null}
    </div>
  ) : (
    <div className="max-w-[92%] space-y-2">
      {m.changes.passo && (
        <p className="text-xs font-semibold uppercase tracking-wide text-accent/80">{m.changes.passo}</p>
      )}
      <div className="whitespace-pre-line rounded-2xl rounded-bl-sm bg-black/[0.04] px-4 py-2 text-sm">
        {m.content}
      </div>
      {m.questions.length > 0 && (
        <ol className="space-y-1.5 pl-1">
          {m.questions.map((q, i) => (
            <li key={i} className="text-sm">
              <span className="font-medium">{q.question}</span>
              <span className="block text-xs text-ink/50">Perché: {q.why}</span>
            </li>
          ))}
        </ol>
      )}
      {m.changes.verifica && <ReviewList card={m.changes.verifica} current={card} />}
      {!!m.changes.aggiornati?.length && (
        // Cosa ha cambiato la persona nei passi della configurazione (ADR 0052): lo ritrova lì, segnato «detto da te».
        <ul className="space-y-0.5 rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          {m.changes.aggiornati.map((u) => (
            <li key={u.passo}>
              <span className="font-semibold">{STEP_TITLES[u.passo] ?? u.passo}:</span> {u.cosa.join('; ')}
            </li>
          ))}
        </ul>
      )}
      {(!!m.changes.processi?.length || !!m.changes.obblighi || !!m.changes.ruoli?.length) && (
        <p className="text-xs text-emerald-700">
          {[
            m.changes.processi?.length ? `nuovi processi: ${m.changes.processi.join(', ')}` : '',
            m.changes.obblighi ? `${m.changes.obblighi} obblighi normativi` : '',
            m.changes.ruoli?.length ? `ruoli: ${m.changes.ruoli.join(', ')}` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {m.sources.length > 0 && (
        <details className="text-xs text-ink/50">
          <summary className="cursor-pointer">Fonti consultate ({m.sources.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {m.sources.map((u) => (
              <li key={u} className="truncate">
                <a
                  className="text-accent hover:underline"
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                >
                  {u}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
      {actions && (
        <SoftwareActions
          m={m}
          onChange={() => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] })}
        />
      )}
    </div>
  );
}
